import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { SEOHead } from "@/components/seo/seo-head";
import { Skeleton } from "@/components/shared/skeleton";
import { api } from "@/lib/api";
import { showToast } from "@/lib/toast";
import { Bell, Check, Trash2 } from "lucide-react";
import { useLanguage } from "@/contexts/language-context";
interface Notification {
  id: string;
  type: string;
  message: string;
  isRead: boolean;
  date: string;
  createdAt: string;
}

export function NotificationsPage() {
  const { t, language } = useLanguage();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<"all" | "unread">("all");

  useEffect(() => {
    fetchNotifications();
  }, [filter]);

  const fetchNotifications = async () => {
    try {
      setIsLoading(true);
      if (filter === "unread") {
        const data = await api.get<{ notifications: Notification[]; count: number }>(
          "/notifications/unread"
        );
        setNotifications(data.notifications);
        setUnreadCount(data.count);
      } else {
        const data = await api.get<{ notifications: Notification[] }>("/notifications");
        setNotifications(data.notifications);
        const unreadData = await api.get<{ count: number }>("/notifications/unread");
        setUnreadCount(unreadData.count);
      }
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleMarkAsRead = async (id: string) => {
    try {
      await api.put(`/notifications/${id}/read`);
      showToast.success(t("notifications.markedAsRead"));
      fetchNotifications();
      // Dispatch event to update navbar badge
      window.dispatchEvent(new Event("notification-updated"));
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
      showToast.error(t("common.error"));
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm(t("common.confirmDelete"))) return;
    try {
      await api.delete(`/notifications/${id}`);
      showToast.success(t("notifications.deleted"));
      fetchNotifications();
      // Dispatch event to update navbar badge
      window.dispatchEvent(new Event("notification-updated"));
    } catch (error) {
      console.error("Failed to delete notification:", error);
      showToast.error(t("common.error"));
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      const unreadNotifications = notifications.filter((n) => !n.isRead);
      await Promise.all(
        unreadNotifications.map((n) => api.put(`/notifications/${n.id}/read`))
      );
      showToast.success(t("notifications.markAllRead"));
      fetchNotifications();
    } catch (error) {
      console.error("Failed to mark all as read:", error);
      showToast.error(t("common.error"));
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t("common.justNow");
    if (diffMins < 60) return `${diffMins} ${t("common.minutesAgo")}`;
    if (diffHours < 24) return `${diffHours} ${t("common.hoursAgo")}`;
    if (diffDays < 7) return `${diffDays} ${t("common.daysAgo")}`;
    return date.toLocaleDateString(language === "id" ? "id-ID" : "en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case "budget_warning":
        return "⚠️";
      case "net_worth_change":
        return "📈";
      case "debt_reminder":
      case "debt_overdue":
        return "💳";
      case "goal_achievement":
        return "🎯";
      default:
        return "🔔";
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <Skeleton className="h-9 w-48 mb-2" />
          <Skeleton className="h-5 w-64" />
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          {Array.from({ length: 2 }).map((_, i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-4 w-24" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-8 w-32" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  const filteredNotifications = filter === "unread"
    ? notifications.filter((n) => !n.isRead)
    : notifications;

  return (
    <>
      <SEOHead
        title="Notifications - Personal Finance App"
        description="View and manage your financial notifications. Stay updated on budget alerts, goal progress, and important financial reminders."
        keywords="notifications, alerts, budget alerts, financial reminders, goal notifications"
        canonicalUrl="/notifications"
      />
      <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">{t("notifications.title")}</h1>
        <p className="text-muted-foreground">
          {t("notifications.subtitle")}
        </p>
      </div>

      {/* Summary */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">{t("notifications.totalNotifications")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{notifications.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">{t("notifications.unread")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-primary">{unreadCount}</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Actions */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>{t("notifications.title")}</CardTitle>
          <div className="flex gap-2">
            <Button
              variant={filter === "all" ? "default" : "outline"}
              size="sm"
              onClick={() => setFilter("all")}
            >
              {t("notifications.all")}
            </Button>
            <Button
              variant={filter === "unread" ? "default" : "outline"}
              size="sm"
              onClick={() => setFilter("unread")}
            >
              {t("notifications.unread")}
            </Button>
            {unreadCount > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleMarkAllAsRead}
              >
                <Check className="h-4 w-4 mr-2" />
                {t("notifications.markAllRead")}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {filteredNotifications.length === 0 ? (
            <div className="text-center py-12">
              <Bell className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">
                {filter === "unread" ? t("notifications.noUnread") : t("notifications.noNotifications")}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredNotifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`flex items-start gap-4 p-4 border rounded-lg ${
                    !notification.isRead ? "bg-muted/50 border-primary/20" : ""
                  }`}
                >
                  <div className="text-2xl">{getNotificationIcon(notification.type)}</div>
                  <div className="flex-1 space-y-1">
                    <p className={`font-medium ${!notification.isRead ? "text-foreground" : "text-muted-foreground"}`}>
                      {notification.message}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatDate(notification.date)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {!notification.isRead && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleMarkAsRead(notification.id)}
                        title={t("notifications.markAllRead")}
                      >
                        <Check className="h-4 w-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(notification.id)}
                      title={t("common.delete")}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
    </>
  );
}
